// @ts-check

// Classes
import {DisplayField} from '@/lib/classes/InfoWindowContent/DisplayField';
import dayjs from 'dayjs';

/**
 * @typedef {import('@/lib/classes/InfoWindowContent/aggregators/shared.js').Aggregator} Aggregator
 */

/**
 * Given a comp object, adds the term to the months and returns the expiration date
 * @param {string} termString the term of the lease
 * @param {string} startDateString the start date of the lease
 * @return {string|undefined} the expiration date of the lease or undefined if the term or start date is missing
 */
const calculateExpirationDate = (termString, startDateString) => {
  if (!termString || !startDateString) return;
  const term = parseInt(termString);
  // dayjs handles adding months better than setting the month on a JavaScript Date object
  return dayjs(startDateString).add(term, 'month').format('YYYY-MM-DD');
};

/**
 * @type {Aggregator}
 */
const expirationDate = {
  function: function(blob) {
    const expirationDateField = blob.displayFields?.find((field) => field.label === 'Expiration Date');
    // If the expiration date field is editable, it means we are treating the display field as a standard field and we
    // should return it as is, without letting the aggregator override it.
    if (expirationDateField?.allowEdit) {
      return [expirationDateField, () => {}];
    }

    const term = blob.displayFields?.find((field) => field.label === 'Term')?.value;
    const startDate = blob.displayFields?.find((field) => field.label === 'Start Date')?.value;
    if (!term || !startDate) return [];

    const expirationDate = calculateExpirationDate(term, startDate);

    return [
      new DisplayField({
        label: 'Expiration Date',
        unit: 'date',
        value: expirationDate,
        allowEdit: false,
        allowLabelEdit: false,
        allowDelete: false,
      }),
      () => {},
    ];
  },
  name: 'expirationDate',
};

/**
 * the current rent will use the start date field, calculated how many years past from then to today, then multiply the
 *  base rent and the annual escalation rent, for however many year past.
 *  For ex. With start date 02/01/2020, base rent $4.00, annual escalation 3% it'll be:
 *  02/01/2020 to 09/25/2024 (today) we're in the 4th year of their engagement.
 *  That means the calculation will be 4.00 * 1.03 * 1.03 * 1.03 = $4.37
 * @type {Aggregator}
 */
const currentRent = {
  function: function(blob) {
    const startDate = blob.displayFields?.find((field) => field.label === 'Start Date')?.value;
    const baseRent = blob.displayFields?.find((field) => field.label === 'Base Rent')?.value;
    const annualEscalations = blob.displayFields?.find((field) => field.label === 'Annual Escalations')?.value;

    if (!startDate || !baseRent || !annualEscalations) return [];

    const term = blob.displayFields?.find((field) => field.label === 'Term')?.value;

    // The expiration date may not be an aggregated field, so only calculate it if it is not already set.
    let expirationDate = blob.displayFields?.find((field) => field.label === 'Expiration Date')?.value;
    if (!expirationDate) {
      expirationDate = calculateExpirationDate(term, startDate);
    }
    if (expirationDate && (new Date(expirationDate) < new Date())) return [];

    const annualEscalation = parseFloat(annualEscalations) / 100;

    const yearsPast = dayjs().diff(dayjs(startDate), 'year');
    const currentRent = baseRent * (1 + annualEscalation) ** yearsPast;

    return [
      new DisplayField({
        label: 'Current Rent',
        unit: 'pricePerSquareFoot',
        value: currentRent,
        allowEdit: false,
        allowLabelEdit: false,
        allowDelete: false,
      }),
      () => {},
    ];
  },
  name: 'currentRent',
};

export {
  calculateExpirationDate,
};

export const aggregators = [
  expirationDate,
  currentRent,
];
